/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {lazy, Suspense} from 'react'
import Dates from 'utils/Dates'
import {BrowserRouter, Navigate, Outlet, Route, Routes} from 'react-router-dom'
import {Flip} from 'react-toastify'
import ToastContainer from 'components/ToastContainer.js'
import Popup from 'components/Popup'
import UiLoader from 'components/Ui/UiLoader'
import Layout from 'layouts/Layout'
import SecondaryPopup from 'components/SecondaryPopup'
import UiThemeProvider from 'theme/UiThemeProvider'
import LoginCheck from 'LoginCheck'
import OnlineReservations from "pages/Front/Reservations/OnlineReservations";
import ForgottenPassword from "pages/Front/ForgottenPassword/ForgottenPassword";

const BookingSellerLoginWithPassword = lazy(
    () => import('pages/Front/Booking/Seller/BookingSellerLoginWithPassword')
)
const BookingSellerReservations = lazy(
    () => import('pages/Front/Booking/Seller/BookingSellerReservations')
)
const BookingSellerReservationDetail = lazy(
    () =>
        import(
            'pages/Front/Booking/Seller/BookingSellerReservationDetail/BookingSellerReservationDetail'
        )
)
const Companies = lazy(() => import('pages/Front/Companies/Companies'))
const Leads = lazy(() => import('pages/Front/Leads/Leads'))
const Opportunities = lazy(
    () => import('pages/Front/Opportunities/Opportunities')
)
const ContactCompaniesList = lazy(
    () => import('pages/Front/Contacts/ContactCompaniesList')
)
const ContactCompanyDetail = lazy(
    () => import('pages/Front/Contacts/ContactCompanyDetail')
)
const EventCalendar = lazy(() => import('pages/Front/Events/EventCalendar'))
const Reminders = lazy(() => import('pages/Front/Reminders/Reminders'))

const Offers = lazy(() => import('pages/Front/Offers/Offers'))
const OfferDetail = lazy(() => import('pages/Front/Offers/OfferDetail'))
const Profile = lazy(() => import('pages/Front/Profile/Profile'))
const OfferEmailTemplatesPage = lazy(
    () => import('pages/Front/Offers/OfferEmailTemplatesPage')
)

const CalendarRedirect = () => {
    const now = Dates.dayjs()
    return (
        <Navigate
            to={`/calendar/month/${now.format('YYYY')}/${now.format('M')}/${now.format('D')}`}
            replace
        />
    )
}

const ProtectedLayout = () => {
    return (
        <Layout>
            <Outlet />
        </Layout>
    )
}

function AppRoutes() {
    return (
        <Suspense fallback={<UiLoader variant="page" />}>
            <Routes>
                <Route path="/" element={<BookingSellerLoginWithPassword />} />
                <Route
                    path="/forgotten-password/:access_token?/:app_id?/:m_clk_id?"
                    element={<ForgottenPassword />}
                />

                <Route element={<ProtectedLayout />}>
                    <Route path="/reservations" element={<BookingSellerReservations />} />
                    <Route path="/online-reservations" element={<OnlineReservations />} />
                    <Route
                        path="/reservations/detail/:reservationId"
                        element={<BookingSellerReservationDetail />}
                    />
                    <Route path="/companies" element={<Companies />} />
                    <Route path="/offers" element={<Offers />} />
                    <Route path="/offers/:offerId" element={<OfferDetail />} />
                    <Route
                        path="/offer-email-templates"
                        element={<OfferEmailTemplatesPage />}
                    />
                    <Route path="/leads" element={<Leads />} />
                    <Route path="/opportunities" element={<Opportunities />} />
                    <Route
                        path="/contact_companies/list"
                        element={<ContactCompaniesList />}
                    />
                    <Route
                        path="/contact_companies/detail/:contactId"
                        element={<ContactCompanyDetail />}
                    />
                    <Route
                        path="/calendar/:view/:year/:month/:day"
                        element={<EventCalendar />}
                    />
                    <Route path="/calendar" element={<CalendarRedirect />} />
                    <Route path="/reminders" element={<Reminders />} />
                    <Route path="/profile" element={<Profile />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    )
}

function App() {
    return (
        <BrowserRouter>
            <LoginCheck />
            <UiThemeProvider>
                <ToastContainer
                    autoClose={5000}
                    draggablePercent={50}
                    newestOnTop={true}
                    pauseOnFocusLoss={false}
                    transition={Flip}
                />
                <AppRoutes />
                <Popup />
                <SecondaryPopup />
            </UiThemeProvider>
        </BrowserRouter>
    )
}

export default App
